import { useState } from 'react';
import { addMonths, differenceInMonths, format, parseISO } from 'date-fns';
import { ADVERSE_EVENT_LABELS } from '../../domain/legalAssumptions';
import { formatBps } from '../../domain/equityEngine';
import type {
  AchievementRoute,
  AdverseEventReason,
  Milestone,
  MilestoneCalculation,
  Scenario,
  ScenarioCalculation,
} from '../../domain/types';

interface ScenarioControlsProps {
  calculation: ScenarioCalculation;
  selectedCalculation: MilestoneCalculation;
  mode?: 'guided' | 'panel';
  onRequestPanel?: () => void;
  onChange: (updater: (scenario: Scenario) => Scenario) => void;
  onReset: () => void;
  onRandom: () => void;
  onExercise: (milestoneId: string) => void;
}

const TIMELINE_START_DATE = '2026-06-20';
const TIMELINE_MONTHS = 42;
const ADVERSE_EVENT_OPTIONS = Object.keys(ADVERSE_EVENT_LABELS) as AdverseEventReason[];
const SEVERE_EVENT_REASONS = new Set<AdverseEventReason>([
  'major_breach',
  'confidentiality_breach',
  'undisclosed_conflict',
  'non_compete_breach',
  'competing_project',
]);

const ROUTE_LABELS: Record<AchievementRoute, string> = {
  industrial_cooperation: '实质产业合作',
  key_hire_six_months: '关键人才连续服务六个月',
};

const DEFAULT_MILESTONE_DATES: Record<string, string> = {
  M1: '2026-06-10',
  M2: '2026-06-14',
  M3: '2026-08-20',
  M4: '2026-10-20',
  M5: '2026-12-20',
  M6: '2027-01-20',
};

type WizardStep = 'script' | 'time' | 'facts' | 'milestones' | 'result';

const WIZARD_STEPS: Array<{ id: WizardStep; label: string; title: string }> = [
  { id: 'script', label: '剧本', title: '先确定要推演的剧本' },
  { id: 'time', label: '时间', title: '拖动到要观察的截至日' },
  { id: 'facts', label: '事实', title: '勾选基础事实' },
  { id: 'milestones', label: '里程碑', title: '确认里程碑和达成日' },
  { id: 'result', label: '结果', title: '查看推演结果' },
];

export function ScenarioControls({
  calculation,
  selectedCalculation,
  mode = 'guided',
  onRequestPanel,
  onChange,
  onReset,
  onRandom,
  onExercise,
}: ScenarioControlsProps) {
  const [activeStep, setActiveStep] = useState<WizardStep>('script');
  const scenario = calculation.scenario;
  const selectedMilestone = selectedCalculation.milestone;
  const timelineOffset = clampMonthOffset(
    differenceInMonths(parseISO(scenario.asOfDate), parseISO(TIMELINE_START_DATE)),
  );
  const hasSevereEvent = SEVERE_EVENT_REASONS.has(scenario.adverseEventReason);
  const story = buildScenarioStory(calculation);
  const activeStepIndex = WIZARD_STEPS.findIndex((step) => step.id === activeStep);
  const currentStep = WIZARD_STEPS[activeStepIndex] ?? WIZARD_STEPS[0];
  const resultMetrics = [
    { label: '成熟权益', value: formatBps(calculation.summary.milestoneEarnedBps) },
    { label: '时间归属', value: formatBps(calculation.summary.timeVestedBps) },
    { label: '当前可行权', value: formatBps(calculation.summary.exercisableBps) },
    { label: '已行权/已取得', value: formatBps(calculation.summary.exercisedBps) },
  ];

  const storyPanel = (
    <section className="story-panel" aria-label="当前剧本">
      <span className="eyebrow">当前剧本</span>
      <p>{story.lead}</p>
      <p>{story.result}</p>
      {story.risk ? <p>{story.risk}</p> : null}
    </section>
  );

  const timeSlider = (
    <section className="time-slider-panel" aria-label="拖动截至时间">
      <div className="slider-heading">
        <span>拖动截至时间</span>
        <strong>{scenario.asOfDate}</strong>
      </div>
      <input
        aria-label="拖动截至时间"
        className="time-slider"
        type="range"
        min="0"
        max={TIMELINE_MONTHS}
        step="1"
        value={timelineOffset}
        onChange={(event) => {
          const nextDate = format(
            addMonths(parseISO(TIMELINE_START_DATE), Number(event.target.value)),
            'yyyy-MM-dd',
          );
          onChange((current) => ({
            ...current,
            asOfDate: nextDate,
          }));
        }}
      />
      <div className="slider-stops" aria-hidden="true">
        <span>现在</span>
        <span>12个月</span>
        <span>24个月</span>
        <span>36个月</span>
      </div>
    </section>
  );

  const basicFacts = (
    <div className="fact-group">
      <span className="eyebrow">基础事实</span>
      <label className="story-check">
        <input
          type="checkbox"
          checked={scenario.conflictCleared}
          onChange={(event) =>
            onChange((current) => ({
              ...current,
              conflictCleared: event.target.checked,
              conflictClearedAt: event.target.checked ? current.conflictClearedAt : null,
            }))
          }
        />
        <span>第3.3条前置限制已清理</span>
      </label>
      <label className="story-check">
        <input
          type="checkbox"
          checked={scenario.serviceActive}
          onChange={(event) =>
            onChange((current) => ({
              ...current,
              serviceActive: event.target.checked,
              adverseEventReason: resolveAdverseEvent({
                current,
                serviceActive: event.target.checked,
              }),
            }))
          }
        />
        <span>乙方持续提供服务</span>
      </label>
      <label className="story-check">
        <input
          type="checkbox"
          checked={scenario.agreementTerminated}
          onChange={(event) =>
            onChange((current) => ({
              ...current,
              agreementTerminated: event.target.checked,
              terminationDate: event.target.checked ? current.terminationDate ?? current.asOfDate : null,
              adverseEventReason: resolveAdverseEvent({
                current,
                agreementTerminated: event.target.checked,
              }),
            }))
          }
        />
        <span>协议已终止</span>
      </label>
      <label className="story-check">
        <input
          type="checkbox"
          checked={hasSevereEvent}
          onChange={(event) =>
            onChange((current) => ({
              ...current,
              adverseEventReason: event.target.checked
                ? 'major_breach'
                : resolveAdverseEvent({
                    current,
                    adverseEventReason: 'none',
                  }),
            }))
          }
        />
        <span>存在重大违约/泄密等严重不利事件</span>
      </label>
    </div>
  );

  const milestoneFacts = (
    <div className="fact-group">
      <span className="eyebrow">里程碑</span>
      <div className="milestone-fact-list">
        {calculation.milestones.map((item) => {
          const isGateMilestone = item.milestone.id === 'M1';
          const dateEnabled = isGateMilestone
            ? scenario.conflictCleared
            : item.milestone.achieved;
          const achievedDate = isGateMilestone
            ? scenario.conflictCleared
              ? scenario.conflictClearedAt ?? ''
              : ''
            : item.milestone.achievedAt ?? '';

          return (
            <div className="milestone-fact-row" key={item.milestone.id}>
              <label className="story-check">
                <input
                  type="checkbox"
                  checked={item.milestone.achieved}
                  disabled={isGateMilestone}
                  onChange={(event) =>
                    onChange((current) => ({
                      ...current,
                      milestones: current.milestones.map((milestone) =>
                        milestone.id === item.milestone.id
                          ? updateMilestoneAchievement(milestone, event.target.checked)
                          : milestone,
                      ),
                    }))
                  }
                />
                <span>
                  {formatMilestoneName(item.milestone)}已完成
                  {isGateMilestone ? '，随第3.3条前置限制同步' : ''}
                </span>
              </label>

              {dateEnabled ? (
                <label className="milestone-date-field">
                  <span>达成日</span>
                  <input
                    type="date"
                    value={achievedDate}
                    onChange={(event) => {
                      const nextDate = event.target.value || null;

                      if (isGateMilestone) {
                        onChange((current) => ({
                          ...current,
                          conflictClearedAt: nextDate,
                        }));
                        return;
                      }

                      onChange((current) => ({
                        ...current,
                        milestones: current.milestones.map((milestone) =>
                          milestone.id === item.milestone.id
                            ? {
                                ...milestone,
                                achievedAt: nextDate,
                                achieved: Boolean(nextDate) || milestone.achieved,
                              }
                            : milestone,
                        ),
                      }));
                    }}
                  />
                </label>
              ) : (
                <p className="milestone-row-hint">勾选完成后再填写达成日。</p>
              )}
            </div>
          );
        })}
      </div>
      <p className="control-note">未填写达成日时，只记录成熟事实，不计算第1.4条时间归属。</p>
    </div>
  );

  const storyActions = (
    <div className="story-actions">
      <button
        className="primary-button"
        type="button"
        disabled={selectedCalculation.exercisableBps <= 0}
        onClick={() => onExercise(selectedMilestone.id)}
      >
        记录当前可行权部分已行权
      </button>
    </div>
  );

  const advancedSettings = (
    <details className="advanced-settings">
      <summary>高级设置</summary>
      <div className="advanced-grid">
        <label>
          清理完成日
          <input
            type="date"
            value={scenario.conflictClearedAt ?? ''}
            disabled={!scenario.conflictCleared}
            onChange={(event) =>
              onChange((current) => ({
                ...current,
                conflictClearedAt: event.target.value || null,
              }))
            }
          />
        </label>

        <label>
          截至日期
          <input
            type="date"
            value={scenario.asOfDate}
            onChange={(event) =>
              onChange((current) => ({
                ...current,
                asOfDate: event.target.value,
              }))
            }
          />
        </label>

        <label>
          终止日期
          <input
            type="date"
            value={scenario.terminationDate ?? ''}
            disabled={!scenario.agreementTerminated}
            onChange={(event) =>
              onChange((current) => ({
                ...current,
                terminationDate: event.target.value || null,
              }))
            }
          />
        </label>

        <label>
          不利事件类型
          <select
            value={scenario.adverseEventReason}
            onChange={(event) =>
              onChange((current) => ({
                ...current,
                adverseEventReason: event.target.value as AdverseEventReason,
              }))
            }
          >
            {ADVERSE_EVENT_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {ADVERSE_EVENT_LABELS[option]}
              </option>
            ))}
          </select>
        </label>

        {selectedMilestone.achievementRoute ? (
          <label>
            M6达成路径
            <select
              value={selectedMilestone.selectedAchievementRoute ?? selectedMilestone.achievementRoute[0]}
              onChange={(event) =>
                onChange((current) => ({
                  ...current,
                  milestones: current.milestones.map((milestone) =>
                    milestone.id === selectedMilestone.id
                      ? {
                          ...milestone,
                          selectedAchievementRoute: event.target.value as AchievementRoute,
                        }
                      : milestone,
                  ),
                }))
              }
            >
              {selectedMilestone.achievementRoute.map((route) => (
                <option key={route} value={route}>
                  {ROUTE_LABELS[route]}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </div>

      <div className="control-actions">
        <button className="ghost-button" type="button" onClick={onRandom}>
          随机情景
        </button>
        <button className="danger-button" type="button" onClick={onReset}>
          重置场景
        </button>
      </div>
    </details>
  );

  if (mode === 'panel') {
    return (
      <section className="scenario-controls is-panel-mode" aria-label="完整推演面板">
        <div className="panel-heading">
          <span className="eyebrow">完整面板</span>
          <h2>在一页中调整全部推演变量</h2>
        </div>

        {storyPanel}
        {timeSlider}
        <section className="fact-board" aria-label="事实勾选">
          {basicFacts}
          {milestoneFacts}
        </section>
        {storyActions}
        {advancedSettings}
      </section>
    );
  }

  function goToStep(offset: number) {
    const nextIndex = Math.max(0, Math.min(WIZARD_STEPS.length - 1, activeStepIndex + offset));
    setActiveStep(WIZARD_STEPS[nextIndex].id);
  }

  function applyAllMilestonesScenario() {
    onChange((current) => ({
      ...current,
      asOfDate: '2029-06-20',
      conflictCleared: true,
      conflictClearedAt: DEFAULT_MILESTONE_DATES.M1,
      serviceActive: true,
      agreementTerminated: false,
      terminationDate: null,
      adverseEventReason: 'none',
      exercisedBpsByMilestone: {},
      milestones: current.milestones.map((milestone) => ({
        ...milestone,
        achieved: true,
        achievedAt: DEFAULT_MILESTONE_DATES[milestone.id] ?? milestone.achievedAt,
      })),
    }));
    setActiveStep('result');
  }

  function applyTerminationRiskScenario() {
    onChange((current) => ({
      ...current,
      conflictCleared: true,
      conflictClearedAt: current.conflictClearedAt ?? DEFAULT_MILESTONE_DATES.M1,
      serviceActive: false,
      agreementTerminated: true,
      terminationDate: current.terminationDate ?? current.asOfDate,
      adverseEventReason: 'service_stopped',
    }));
    setActiveStep('result');
  }

  function renderGuidedStep() {
    if (activeStep === 'script') {
      return (
        <>
          <div className="wizard-card-heading">
            <span className="eyebrow">第 1 步</span>
            <h3>{currentStep.title}</h3>
            <p>先用一句话看懂当前状态。需要快速试算时，可以直接套用一个剧本。</p>
          </div>
          {storyPanel}
          <div className="preset-grid" aria-label="快速剧本">
            <button className="preset-button is-primary" type="button" onClick={() => goToStep(1)}>
              <strong>保留当前剧本</strong>
              <span>继续用现在页面中的事实推进。</span>
            </button>
            <button className="preset-button" type="button" onClick={applyAllMilestonesScenario}>
              <strong>全部顺利达成</strong>
              <span>六项里程碑均完成，并观察满36个月后的权益。</span>
            </button>
            <button className="preset-button" type="button" onClick={applyTerminationRiskScenario}>
              <strong>终止/服务停止风险</strong>
              <span>观察普通终止或服务停止后的失效影响。</span>
            </button>
            <button className="preset-button" type="button" onClick={onRandom}>
              <strong>随机情景</strong>
              <span>用于快速压力测试，不建议作为正式展示结论。</span>
            </button>
          </div>
        </>
      );
    }

    if (activeStep === 'time') {
      return (
        <>
          <div className="wizard-card-heading">
            <span className="eyebrow">第 2 步</span>
            <h3>{currentStep.title}</h3>
            <p>时间会影响第1.4条归属进度。先选观察日期，再判断截至该日能取得多少权益。</p>
          </div>
          {timeSlider}
        </>
      );
    }

    if (activeStep === 'facts') {
      return (
        <>
          <div className="wizard-card-heading">
            <span className="eyebrow">第 3 步</span>
            <h3>{currentStep.title}</h3>
            <p>基础事实决定模型是否放行、是否进入终止或不利事件情景。</p>
          </div>
          <section className="fact-board is-guided" aria-label="基础事实勾选">
            {basicFacts}
          </section>
        </>
      );
    }

    if (activeStep === 'milestones') {
      return (
        <>
          <div className="wizard-card-heading">
            <span className="eyebrow">第 4 步</span>
            <h3>{currentStep.title}</h3>
            <p>只勾选已经发生的事项。勾选后再填写真实达成日，避免系统替你假设日期。</p>
          </div>
          <section className="fact-board is-guided" aria-label="里程碑勾选">
            {milestoneFacts}
          </section>
        </>
      );
    }

    return (
      <>
        <div className="wizard-card-heading">
          <span className="eyebrow">第 5 步</span>
          <h3>{currentStep.title}</h3>
          <p>这里给出可读结论；右侧仍保留实时汇总和时间线，方便核对因果关系。</p>
        </div>
        {storyPanel}
        <div className="guided-result-grid" aria-label="结果摘要">
          {resultMetrics.map((metric) => (
            <div key={metric.label}>
              <span>{metric.label}</span>
              <strong>{metric.value}</strong>
            </div>
          ))}
        </div>
        {storyActions}
        {advancedSettings}
      </>
    );
  }

  return (
    <section className="scenario-controls is-guided-mode" aria-label="快速推演向导">
      <div className="panel-heading">
        <span className="eyebrow">推演入口</span>
        <h2>按步骤完成一次推演</h2>
      </div>

      <nav className="wizard-steps" aria-label="快速推演步骤">
        {WIZARD_STEPS.map((step, index) => (
          <button
            className={`${activeStep === step.id ? 'is-active' : ''} ${
              index < activeStepIndex ? 'is-done' : ''
            }`}
            type="button"
            key={step.id}
            onClick={() => setActiveStep(step.id)}
          >
            <span>{index + 1}</span>
            <strong>{step.label}</strong>
          </button>
        ))}
      </nav>

      <section className="wizard-card" aria-label={currentStep.title}>
        {renderGuidedStep()}
      </section>

      <div className="wizard-actions">
        <button
          className="ghost-button"
          type="button"
          disabled={activeStepIndex === 0}
          onClick={() => goToStep(-1)}
        >
          上一步
        </button>
        {activeStep === 'result' ? (
          <button className="primary-button" type="button" onClick={() => setActiveStep('script')}>
            重新推演
          </button>
        ) : (
          <button className="primary-button" type="button" onClick={() => goToStep(1)}>
            下一步
          </button>
        )}
        {onRequestPanel ? (
          <button className="ghost-button" type="button" onClick={onRequestPanel}>
            查看完整面板
          </button>
        ) : null}
      </div>
    </section>
  );
}

function clampMonthOffset(value: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }

  return Math.max(0, Math.min(TIMELINE_MONTHS, value));
}

function updateMilestoneAchievement(
  milestone: Milestone,
  achieved: boolean,
): Milestone {
  return {
    ...milestone,
    achieved,
    achievedAt: achieved ? milestone.achievedAt : null,
  };
}

function resolveAdverseEvent({
  current,
  agreementTerminated = current.agreementTerminated,
  serviceActive = current.serviceActive,
  adverseEventReason = current.adverseEventReason,
}: {
  current: Scenario;
  agreementTerminated?: boolean;
  serviceActive?: boolean;
  adverseEventReason?: AdverseEventReason;
}): AdverseEventReason {
  if (SEVERE_EVENT_REASONS.has(adverseEventReason)) {
    return adverseEventReason;
  }

  if (agreementTerminated) {
    return 'ordinary_termination';
  }

  if (!serviceActive) {
    return 'service_stopped';
  }

  return 'none';
}

function buildScenarioStory(calculation: ScenarioCalculation): {
  lead: string;
  result: string;
  risk: string | null;
} {
  const { scenario, summary } = calculation;
  const achievedMilestones = calculation.milestones.filter((item) => item.milestoneEarnedBps > 0);
  const achievedText =
    achievedMilestones.length > 0
      ? achievedMilestones.map((item) => formatMilestoneName(item.milestone)).join('、')
      : '尚无里程碑';

  if (!scenario.conflictCleared) {
    return {
      lead: `截至 ${scenario.asOfDate}，第3.3条前置限制尚未清理。`,
      result: `即使其他事项已经录入，当前仍不能计入乙方可主张的有效孵化权益/认股权。最高权益为 ${formatBps(
        summary.potentialBps,
      )}，当前可行权为 ${formatBps(summary.exercisableBps)}。`,
      risk: '先勾选并确认前置限制清理后，再看六项里程碑和时间归属。',
    };
  }

  const lead = `截至 ${scenario.asOfDate}，前置限制已清理，${achievedText} 已计入里程碑成熟。`;
  const result =
    summary.timeVestedBps > 0
      ? `当前成熟权益为 ${formatBps(summary.milestoneEarnedBps)}，其中已完成时间归属 ${formatBps(
          summary.timeVestedBps,
        )}，当前可行权 ${formatBps(summary.exercisableBps)}。`
      : `当前成熟权益为 ${formatBps(
          summary.milestoneEarnedBps,
        )}，但尚未走满第1.4条12个月归属期，因此当前可行权为 ${formatBps(
          summary.exercisableBps,
        )}。`;

  if (scenario.agreementTerminated || scenario.adverseEventReason !== 'none') {
    return {
      lead,
      result,
      risk: `当前情景包含“${ADVERSE_EVENT_LABELS[scenario.adverseEventReason]}”，请重点查看失效和回购/回收风险。`,
    };
  }

  return {
    lead,
    result,
    risk: null,
  };
}

function formatMilestoneName(milestone: Milestone): string {
  return `${milestone.shortTitle}（${milestone.id}）`;
}
